import { calculateTotalFeesDue, getStudentBill } from '../utils/billUtils';

class AccountsReceivableService {
  // Calculate outstanding fees for all students
  // Updated to use individual bills when available
  calculateOutstandingFees(students, classData) {
    const outstandingByClass = {};
    const outstandingByTerm = {};
    let totalOutstanding = 0;
    const debtorsList = [];

    students.forEach(student => {
      if (student.status !== 'Active') return;

      // Find class data for this student
      const studentClass = classData.find(cls => cls.classname === student.class_);
      const classFees = studentClass ? parseFloat(studentClass.classfees) : 0;

      // Calculate outstanding balance
      let totalDue = classFees;
      let amountPaid = 0;
      let billSource = 'default';

      if (student.finance && student.finance.length > 0) {
        const latestFinance = student.finance[student.finance.length - 1];
        
        // Get term from latest finance record
        let term = null;
        if (latestFinance.term) {
          term = typeof latestFinance.term === 'object' 
            ? (latestFinance.term.name || latestFinance.term.term || latestFinance.term.value)
            : latestFinance.term;
        }
        
        // Use bill utility to get correct bill (individual -> class -> default)
        if (studentClass && term) {
          totalDue = calculateTotalFeesDue(student, studentClass, term);
          const bill = getStudentBill(student, studentClass, term);
          if (bill) {
            billSource = bill.source;
          }
        } else {
          // Fallback to original logic if no term/class data
          const totalFees = parseFloat(latestFinance.totalfees) || 0;
          const arrears = parseFloat(latestFinance.Arrears) || 0;
          totalDue = totalFees + arrears;
        }

        if (latestFinance.payment && Array.isArray(latestFinance.payment)) {
          latestFinance.payment.forEach(payment => {
            if (payment.des !== "Discount") {
              amountPaid += parseFloat(payment.amountpaid) || 0;
            }
          });
        }
      }

      const outstanding = Math.max(0, totalDue - amountPaid);

      if (outstanding > 0) {
        totalOutstanding += outstanding;

        // Track by class
        if (!outstandingByClass[student.class_]) {
          outstandingByClass[student.class_] = 0;
        }
        outstandingByClass[student.class_] += outstanding;

        // Track by term
        if (student.finance && student.finance.length > 0) {
          const latestFinance = student.finance[student.finance.length - 1];
          if (latestFinance.term) {
            if (!outstandingByTerm[latestFinance.term]) {
              outstandingByTerm[latestFinance.term] = 0;
            }
            outstandingByTerm[latestFinance.term] += outstanding;
          }
        }

        // Add to debtors list
        debtorsList.push({
          studentId: student.ID || student._id,
          studentName: `${student.lastname || ''} ${student.othernames || ''}`.trim(),
          class: student.class_,
          totalDue,
          amountPaid,
          outstanding,
          billSource, // Indicate if using individual, class, or default bill
          contact: student.guardiancontact || student.mothercontact || student.fathercontact || 'N/A',
          lastPaymentDate: this.getLastPaymentDate(student)
        });
      }
    });

    return {
      totalOutstanding,
      outstandingByClass,
      outstandingByTerm,
      debtorsList: debtorsList.sort((a, b) => b.outstanding - a.outstanding),
      debtorCount: debtorsList.length
    };
  }

  // Get last payment date for a student
  getLastPaymentDate(student) {
    if (!student.finance || student.finance.length === 0) return null;

    let lastDate = null;
    student.finance.forEach(financeRecord => {
      if (financeRecord.payment && Array.isArray(financeRecord.payment)) {
        financeRecord.payment.forEach(payment => {
          if (payment.date) {
            const paymentDate = new Date(payment.date);
            if (!lastDate || paymentDate > lastDate) {
              lastDate = paymentDate;
            }
          }
        });
      }
    });

    return lastDate;
  }

  // Calculate aging analysis (30, 60, 90+ days)
  calculateAgingAnalysis(debtorsList) {
    const today = new Date();
    const aging = {
      current: { amount: 0, count: 0 }, // 0-30 days
      days30: { amount: 0, count: 0 },  // 31-60 days
      days60: { amount: 0, count: 0 },   // 61-90 days
      days90: { amount: 0, count: 0 }    // 90+ days
    };

    debtorsList.forEach(debtor => {
      const daysSinceLastPayment = debtor.lastPaymentDate
        ? Math.floor((today - new Date(debtor.lastPaymentDate)) / (1000 * 60 * 60 * 24))
        : 999; // If no payment date, consider it very old

      if (daysSinceLastPayment <= 30) {
        aging.current.amount += debtor.outstanding;
        aging.current.count++;
      } else if (daysSinceLastPayment <= 60) {
        aging.days30.amount += debtor.outstanding;
        aging.days30.count++;
      } else if (daysSinceLastPayment <= 90) {
        aging.days60.amount += debtor.outstanding;
        aging.days60.count++;
      } else {
        aging.days90.amount += debtor.outstanding;
        aging.days90.count++;
      }
    });

    return aging;
  }

  // Get complete accounts receivable summary
  getAccountsReceivableSummary(students, classData) {
    const outstanding = this.calculateOutstandingFees(students, classData);
    const aging = this.calculateAgingAnalysis(outstanding.debtorsList);

    return {
      ...outstanding,
      aging
    };
  }
}

export default new AccountsReceivableService();
